import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const Activities = () => {
  const { loading: authLoading, isAuthenticated } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [insights, setInsights] = useState({
    totalActivities: 0,
    mostActiveUser: null,
    todayActivities: 0,
    weeklyActivities: 0,
    activityByAction: {},
    recentTrend: 'stable'
  });

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchActivities();
      fetchActivityInsights();
    }
  }, [currentPage, isAuthenticated, authLoading]);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      console.log('Fetching activities...');
      const response = await axios.get(`/activities?page=${currentPage}&per_page=20`);
      setActivities(response.data.activities);
      setPagination({
        total: response.data.total,
        pages: response.data.pages,
        current_page: response.data.current_page
      });
      console.log('Activities loaded:', response.data.activities.length);
    } catch (error) {
      setError('Failed to load activities');
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityInsights = async () => {
    try {
      console.log('Fetching activity insights...');
      const response = await axios.get('/activities?page=1&per_page=1000'); // Get more data for insights
      const allActivities = response.data.activities;
      
      // Calculate insights
      const today = new Date();
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      
      const todayActivities = allActivities.filter(activity => 
        new Date(activity.created_at).toDateString() === today.toDateString()
      ).length;
      
      const weeklyActivities = allActivities.filter(activity => 
        new Date(activity.created_at) >= weekAgo
      ).length;
      
      // Find most active user
      const userActivityCount = {};
      allActivities.forEach(activity => {
        const userId = activity.user_id;
        userActivityCount[userId] = (userActivityCount[userId] || 0) + 1;
      });
      
      let mostActiveUser = null;
      let maxActivities = 0;
      Object.entries(userActivityCount).forEach(([userId, count]) => {
        if (count > maxActivities) {
          maxActivities = count;
          mostActiveUser = allActivities.find(a => a.user_id == userId)?.user_name || 'Unknown';
        }
      });
      
      // Activity by action type
      const activityByAction = {};
      allActivities.forEach(activity => {
        activityByAction[activity.action] = (activityByAction[activity.action] || 0) + 1;
      });
      
      // Calculate trend (comparing this week vs last week)
      const lastWeekStart = new Date(weekAgo.getTime() - 7 * 24 * 60 * 60 * 1000);
      const lastWeekActivities = allActivities.filter(activity => {
        const activityDate = new Date(activity.created_at);
        return activityDate >= lastWeekStart && activityDate < weekAgo;
      }).length;
      
      let recentTrend = 'stable';
      if (weeklyActivities > lastWeekActivities * 1.1) {
        recentTrend = 'increasing';
      } else if (weeklyActivities < lastWeekActivities * 0.9) {
        recentTrend = 'decreasing';
      }
      
      setInsights({
        totalActivities: response.data.total,
        mostActiveUser: mostActiveUser,
        mostActiveUserCount: maxActivities,
        todayActivities,
        weeklyActivities,
        activityByAction,
        recentTrend
      });
      
      console.log('Activity insights calculated:', {
        totalActivities: response.data.total,
        todayActivities,
        weeklyActivities,
        mostActiveUser
      });
      
    } catch (error) {
      console.error('Error fetching activity insights:', error);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActivityIcon = (action) => {
    const icons = {
      'created': '🆕',
      'updated': '✏️',
      'commented': '💬',
      'assigned': '👤',
      'attachment': '📎',
      'closed': '✅',
      'reopened': '🔄'
    };
    return icons[action] || '📝';
  };

  const getActivityColor = (action) => {
    const colors = {
      'created': 'text-green-600',
      'updated': 'text-blue-600',
      'commented': 'text-purple-600',
      'assigned': 'text-orange-600',
      'attachment': 'text-gray-600',
      'closed': 'text-green-700',
      'reopened': 'text-yellow-600'
    };
    return colors[action] || 'text-gray-600';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'increasing': return '📈';
      case 'decreasing': return '📉';
      default: return '📊';
    }
  };

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'increasing': return 'text-green-600';
      case 'decreasing': return 'text-red-600';
      default: return 'text-blue-600';
    }
  };

  if (authLoading) {
    return (
      <div className="main-content">
        <div className="flex items-center justify-center py-8">
          <div className="spinner"></div>
          <span className="ml-2">Authenticating...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Activity History</h1>
        <p className="text-gray-600">Track all changes and updates across the system</p>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="spinner"></div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          {error}
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="card">
            <div className="card-header">
              <div className="flex justify-between items-center">
                <h2 className="card-title">Recent Activities</h2>
                <div className="text-sm text-gray-600">
                  Showing {activities.length} of {pagination.total} activities
                </div>
              </div>
            </div>
            <div className="card-body">
              {activities.length > 0 ? (
                <div className="space-y-4">
                  {activities.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      {/* Activity Icon */}
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full text-sm">
                        {getActivityIcon(activity.action)}
                      </div>

                      {/* Activity Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">
                            {activity.user_name}
                          </span>
                          <span className={`text-sm font-medium ${getActivityColor(activity.action)}`}>
                            {activity.action}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatDate(activity.created_at)}
                          </span>
                        </div>
                        
                        <p className="text-gray-700 mb-2">
                          {activity.description}
                        </p>

                        <div className="flex items-center gap-2">
                          <Link
                            to={`/issues/${activity.issue_id}`}
                            className="text-sm text-primary-color hover:underline"
                          >
                            View Issue #{activity.issue_id}
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-4xl mb-4">📋</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No activities found
                  </h3>
                  <p className="text-gray-600 mb-4">
                    System activities will appear here as users interact with issues.
                  </p>
                  <Link to="/create-issue" className="btn btn-primary">
                    Create First Issue
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
                className="btn btn-outline btn-sm"
              >
                Previous
              </button>
              
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  let page;
                  if (pagination.pages <= 5) {
                    page = i + 1;
                  } else if (pagination.current_page <= 3) {
                    page = i + 1;
                  } else if (pagination.current_page >= pagination.pages - 2) {
                    page = pagination.pages - 4 + i;
                  } else {
                    page = pagination.current_page - 2 + i;
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`btn btn-sm ${
                        page === pagination.current_page 
                          ? 'btn-primary' 
                          : 'btn-outline'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.pages}
                className="btn btn-outline btn-sm"
              >
                Next
              </button>
            </div>
          )}

          {/* Activity Insights */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="card">
              <div className="card-body text-center">
                <div className="text-3xl mb-2">📊</div>
                <div className="text-2xl font-bold text-blue-600 mb-1">
                  {insights.totalActivities}
                </div>
                <div className="text-sm text-gray-600">Total Activities</div>
                <div className="text-xs text-gray-500 mt-1">All time</div>
              </div>
            </div>
            
            <div className="card">
              <div className="card-body text-center">
                <div className="text-3xl mb-2">🔥</div>
                <div className="text-lg font-bold text-orange-600 mb-1">
                  {insights.mostActiveUser || 'None'}
                </div>
                <div className="text-sm text-gray-600">Most Active User</div>
                <div className="text-xs text-gray-500 mt-1">
                  {insights.mostActiveUserCount || 0} activities
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="card-body text-center">
                <div className="text-3xl mb-2">{getTrendIcon(insights.recentTrend)}</div>
                <div className={`text-2xl font-bold mb-1 ${getTrendColor(insights.recentTrend)}`}>
                  {insights.weeklyActivities}
                </div>
                <div className="text-sm text-gray-600">This Week</div>
                <div className="text-xs text-gray-500 mt-1 capitalize">
                  {insights.recentTrend} trend
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="card-body text-center">
                <div className="text-3xl mb-2">⏰</div>
                <div className="text-2xl font-bold text-green-600 mb-1">
                  {insights.todayActivities}
                </div>
                <div className="text-sm text-gray-600">Today</div>
                <div className="text-xs text-gray-500 mt-1">Recent activity</div>
              </div>
            </div>
          </div>

          {/* Activity Breakdown */}
          {Object.keys(insights.activityByAction).length > 0 && (
            <div className="card mt-6">
              <div className="card-header">
                <h3 className="card-title">Activity Breakdown</h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {Object.entries(insights.activityByAction).map(([action, count]) => (
                    <div key={action} className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl mb-1">{getActivityIcon(action)}</div>
                      <div className="text-lg font-semibold">{count}</div>
                      <div className="text-sm text-gray-600 capitalize">{action}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Activities; 